import { AltApplication } from '@repo/alt/package'
import bridge from '@vkontakte/vk-bridge'
import { defineNuxtPlugin, useAppConfig, useRuntimeConfig } from 'nuxt/app'

import { bindPlatformPayload } from '../bindPlatformPayload'
import { createPlatformAdapter } from '../createPlatformAdapter'
import {
  detectPlatform,
  isTruthyPublicFlag,
} from '../detectPlatform'
import type { IPlatformAdapter } from '../ports/IPlatformAdapter'
import type { PlatformLaunchPayload } from '../ports/types'
import { resolveDevPayload } from '../resolveDevLaunchPayload'
import { resolveShowcaseHost } from '../resolveShowcaseHost'
import { PLATFORM_ADAPTER } from '../tokens'

declare module 'nuxt/app' {
  interface NuxtApp {
    $platform: IPlatformAdapter
    $platformPayload: PlatformLaunchPayload
  }
}

declare module 'vue' {
  interface ComponentCustomProperties {
    $platform: IPlatformAdapter
    $platformPayload: PlatformLaunchPayload
  }
}

/**
 * Detect host, construct adapter, `init()`, provide + rebind DI for later tasks (03/08/04).
 */
export default defineNuxtPlugin({
  name: 'platform-adapter:plugin',
  dependsOn: ['alt-container:plugin'],
  async setup(nuxtApp) {
    const config = useRuntimeConfig()
    const publicConfig = config.public as {
      miniappDev?: unknown
      miniappDevStartParam?: unknown
      miniappDevPayload?: unknown
      tgAppId?: unknown
      vkAppId?: unknown
    }

    const kind = detectPlatform({
      miniappDev: isTruthyPublicFlag(publicConfig.miniappDev),
      isDevBuild: import.meta.dev,
      // Injected seam — keeps detectPlatform free of a hard vk-bridge import (DIP).
      isVkEmbedded: () => bridge.isEmbedded(),
    })

    const startParamOverride
      = typeof publicConfig.miniappDevStartParam === 'string'
        && publicConfig.miniappDevStartParam.trim()
        ? publicConfig.miniappDevStartParam.trim()
        : null

    const payloadOverride
      = typeof publicConfig.miniappDevPayload === 'string'
        && publicConfig.miniappDevPayload.trim()
        ? publicConfig.miniappDevPayload.trim()
        : null

    const appConfig = useAppConfig()
    appConfig.host = resolveShowcaseHost({
      kind,
      devPayload: kind === 'dev' ? resolveDevPayload(payloadOverride) : null,
      tgAppId: typeof publicConfig.tgAppId === 'string' ? publicConfig.tgAppId : '',
      vkAppId: typeof publicConfig.vkAppId === 'string' ? publicConfig.vkAppId : '',
    }) ?? ''

    const adapter = createPlatformAdapter(kind, {
      startParam: kind === 'dev' ? startParamOverride : null,
      payload: kind === 'dev' ? payloadOverride : null,
    })
    const payload = await adapter.init()

    const alt = nuxtApp.$alt as AltApplication
    if (alt.container.isBound(PLATFORM_ADAPTER)) {
      alt.container.rebindSync(PLATFORM_ADAPTER).toConstantValue(adapter)
    }
    else {
      alt.container.bind(PLATFORM_ADAPTER).toConstantValue(adapter)
    }

    bindPlatformPayload(nuxtApp as { $alt?: unknown }, payload)

    if (import.meta.dev) {
      ;(window as Window & { __platform?: IPlatformAdapter }).__platform = adapter
    }

    return {
      provide: {
        platform: adapter,
        platformPayload: payload,
      },
    }
  },
})
